<script setup lang="ts">
/**
 * SurveyBlueprintEditor
 *
 * Wrapper component that provides edit modal logic for survey blueprint
 * questions and answers. Exposes openQuestionModal/openAnswerModal methods
 * to child content via a scoped slot, and renders the two styled-modal
 * instances for editing.
 */
import { ref, nextTick } from "vue";

// CSRF token read from the meta tag at component init time
const csrfToken =
    document.querySelector('meta[name="token"]')?.getAttribute("content") ?? "";

// --- Question modal state ---
const questionModal = ref<any>(null);
const currentQuestion = ref<number | null>(null);
const questionText = ref("");
const questionRequired = ref(false);
const questionMultipleChoice = ref(false);

// --- Answer modal state ---
const answerModal = ref<any>(null);
const currentAnswer = ref<number | null>(null);
const answerText = ref("");
const answerFreeform = ref(false);
const answerAdditional = ref(false);
const answerPrompt = ref("");

/**
 * Opens the question edit modal and populates form fields
 * by reading hidden input values from the question card DOM.
 */
function openQuestionModal(questionId: number): void {
    const card = document.querySelector(
        `[data-question-id="${questionId}"]`
    );
    if (card) {
        questionText.value =
            (
                card.querySelector(
                    "input[data-question-text]"
                ) as HTMLInputElement
            )?.value || "";
        questionRequired.value =
            (
                card.querySelector(
                    "input[data-question-required]"
                ) as HTMLInputElement
            )?.value === "1";
        questionMultipleChoice.value =
            (
                card.querySelector(
                    "input[data-question-multiple]"
                ) as HTMLInputElement
            )?.value === "1";
    }
    currentQuestion.value = questionId;
    nextTick(() => {
        questionModal.value?.openModal();
    });
}

/**
 * Opens the answer edit modal and populates form fields
 * by reading hidden input values from the answer row DOM.
 */
function openAnswerModal(answerId: number): void {
    const card = document.querySelector(`[data-answer-id="${answerId}"]`);
    if (card) {
        answerText.value =
            (
                card.querySelector(
                    "input[data-answer-text]"
                ) as HTMLInputElement
            )?.value || "";
        answerFreeform.value =
            (
                card.querySelector(
                    "input[data-answer-freeform]"
                ) as HTMLInputElement
            )?.value === "1";
        answerAdditional.value =
            (
                card.querySelector(
                    "input[data-answer-additional]"
                ) as HTMLInputElement
            )?.value === "1";
        answerPrompt.value =
            (
                card.querySelector(
                    "input[data-answer-prompt]"
                ) as HTMLInputElement
            )?.value || "";
    }
    currentAnswer.value = answerId;
    nextTick(() => {
        answerModal.value?.openModal();
    });
}
</script>

<template>
    <div>
        <!-- Scoped slot exposes modal open methods to parent Blade template -->
        <slot
            :openQuestionModal="openQuestionModal"
            :openAnswerModal="openAnswerModal"
        />

        <!-- Question Edit Modal -->
        <styled-modal :start-open="false" :is-wide="true" ref="questionModal">
            <template v-slot:panel="slotProps">
                <div
                    class="text-left bg-blue-700 text-white p-4 flex justify-between items-center -m-6 mb-4 rounded-t-md"
                >
                    <h3 class="text-white text-lg font-bold m-0 pl-2">Edit Question</h3>
                    <button
                        @click="slotProps.events.closeModal()"
                        class="text-blue-100 hover:text-white text-2xl leading-none transition-colors"
                    >
                        &times;
                    </button>
                </div>

                <form
                    v-if="currentQuestion"
                    :action="
                        '/admin/settings/survey/blueprints/questions/' +
                        currentQuestion
                    "
                    method="POST"
                    class="text-left p-4 space-y-3"
                >
                    <input
                        type="hidden"
                        name="_token"
                        :value="csrfToken"
                    />
                    <input type="hidden" name="_method" value="PUT" />

                    <div>
                        <label
                            class="block text-sm font-semibold text-gray-700 mb-1"
                            >Question Text</label
                        >
                        <textarea
                            v-model="questionText"
                            name="question"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows="2"
                            required
                        ></textarea>
                    </div>

                    <fieldset>
                        <legend
                            class="text-sm font-semibold text-gray-700 mb-2"
                        >
                            Question Options
                        </legend>
                        <div class="flex items-center gap-6">
                            <label class="flex items-center cursor-pointer">
                                <input
                                    v-model="questionRequired"
                                    type="checkbox"
                                    name="required"
                                    value="1"
                                    class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                                />
                                <span class="ml-2 text-sm text-gray-700"
                                    >Required Question</span
                                >
                            </label>
                            <label class="flex items-center cursor-pointer">
                                <input
                                    v-model="questionMultipleChoice"
                                    type="checkbox"
                                    name="multiple_choice"
                                    value="1"
                                    class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                                />
                                <span class="ml-2 text-sm text-gray-700"
                                    >Multiple Choice</span
                                >
                            </label>
                        </div>
                    </fieldset>

                    <div class="flex gap-2 pt-3 border-t">
                        <button
                            type="submit"
                            class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        >
                            Save Changes
                        </button>
                        <button
                            type="button"
                            @click="slotProps.events.closeModal()"
                            class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </template>
        </styled-modal>

        <!-- Answer Edit Modal -->
        <styled-modal :start-open="false" :is-wide="true" ref="answerModal">
            <template v-slot:panel="slotProps">
                <div
                    class="text-left bg-green-700 text-white p-4 flex justify-between items-center -m-6 mb-4 rounded-t-md"
                >
                    <h3 class="text-white text-lg font-bold m-0 pl-2">Edit Answer</h3>
                    <button
                        @click="slotProps.events.closeModal()"
                        class="text-green-100 hover:text-white text-2xl leading-none transition-colors"
                    >
                        &times;
                    </button>
                </div>

                <form
                    v-if="currentAnswer"
                    :action="
                        '/admin/settings/survey/blueprints/answers/' +
                        currentAnswer
                    "
                    method="POST"
                    class="text-left p-4 space-y-3"
                >
                    <input
                        type="hidden"
                        name="_token"
                        :value="csrfToken"
                    />
                    <input type="hidden" name="_method" value="PUT" />

                    <div>
                        <label
                            class="block text-sm font-semibold text-gray-700 mb-1"
                            >Answer Text</label
                        >
                        <input
                            v-model="answerText"
                            type="text"
                            name="answer"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            required
                        />
                    </div>

                    <fieldset>
                        <legend
                            class="text-sm font-semibold text-gray-700 mb-2"
                        >
                            Response Options
                        </legend>
                        <div class="flex items-center gap-6">
                            <label class="flex items-center cursor-pointer">
                                <input
                                    v-model="answerFreeform"
                                    type="checkbox"
                                    name="has_freeform_response"
                                    value="1"
                                    class="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500"
                                />
                                <span class="ml-2 text-sm text-gray-700"
                                    >Freeform response</span
                                >
                            </label>
                            <label class="flex items-center cursor-pointer">
                                <input
                                    v-model="answerAdditional"
                                    type="checkbox"
                                    name="has_additional_response"
                                    value="1"
                                    class="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500"
                                />
                                <span class="ml-2 text-sm text-gray-700"
                                    >Additional response (follow-up)</span
                                >
                            </label>
                        </div>
                    </fieldset>

                    <template v-if="answerAdditional">
                        <div>
                            <label
                                class="block text-sm font-semibold text-gray-700 mb-1"
                                >Follow-up Prompt</label
                            >
                            <input
                                v-model="answerPrompt"
                                type="text"
                                name="additional_response_prompt"
                                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="e.g., Please specify..."
                            />
                        </div>
                    </template>

                    <div class="flex gap-2 pt-3 border-t">
                        <button
                            type="submit"
                            class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                        >
                            Save Changes
                        </button>
                        <button
                            type="button"
                            @click="slotProps.events.closeModal()"
                            class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </template>
        </styled-modal>
    </div>
</template>
